import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import {
  postGirviDashboard,
  isErpApiSuccess,
  getErpRequestErrorMessage,
} from "../../erpClient";
import { getUserData } from "../const";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import { formatGirviCurrency, formatGirviWeight } from "./girviTheme";
import TransactionHistoryTable from "./TransactionHistoryTable";
import "./girvi.css";

function dashNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function GirviDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  const canList = hasPermission(PERMISSIONS.GIRVITABLE);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const user = getUserData();
      const branchIdRaw = user?.branch_id ?? user?.branchId ?? 1;
      const branch_id =
        branchIdRaw !== "" && branchIdRaw != null
          ? Number(branchIdRaw) || 1
          : 1;

      const data = await postGirviDashboard({
        branch_id,
      });

      if (isErpApiSuccess(data) || data?.message === "radha") {
        setDashboardData(data.data ?? data);
      } else {
        toast.error(data?.message || "Failed to fetch dashboard data");
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error(
        getErpRequestErrorMessage(error, "Failed to fetch dashboard data"),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    const handleApiUrlChange = () => {
      fetchDashboardData();
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, []);

  const remaining = dashNum(dashboardData?.remaining_amt);
  const principal = dashNum(dashboardData?.principal_given);
  const recoveredPct =
    principal > 0
      ? Math.min(100, Math.max(0, ((principal - remaining) / principal) * 100))
      : 0;

  const metals = [
    {
      key: "gold",
      name: "Gold",
      icon: "solar:crown-line-linear",
      amount: dashNum(dashboardData?.total_gold_amt),
      weight: dashNum(dashboardData?.total_gold_gm),
      items: dashNum(dashboardData?.gold_items),
    },
    {
      key: "silver",
      name: "Silver",
      icon: "solar:star-linear",
      amount: dashNum(dashboardData?.total_silver_amt),
      weight: dashNum(dashboardData?.total_silver_gm),
      items: dashNum(dashboardData?.silver_items),
    },
    {
      key: "all",
      name: "All metals",
      icon: "solar:box-minimalistic-linear",
      amount: dashNum(dashboardData?.total_assets_amt),
      weight: dashNum(dashboardData?.total_gm),
      items: dashNum(dashboardData?.total_items),
    },
  ];

  return (
    <div className="body-wrapper">
      <div className="container-fluid py-4">
        <div className="d-flex flex-column gap-4">
        <div className="row g-4">
          <div className="col-12">
            <section className="girvi-ledger">
              <div className="girvi-ledger-main">
                <p className="girvi-ledger-kicker">Outstanding</p>
                <p className="girvi-ledger-hero">
                  {loading ? (
                    <span className="girvi-ledger-skel" />
                  ) : (
                    formatGirviCurrency(remaining)
                  )}
                </p>
                <div
                  className="girvi-ledger-track"
                  role="progressbar"
                  aria-valuenow={Math.round(recoveredPct)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label="Principal recovered"
                >
                  <span style={{ width: loading ? "0%" : `${recoveredPct}%` }} />
                </div>
              </div>
              <div className="girvi-ledger-aside">
                <div className="girvi-ledger-stat">
                  <span>Active dihran</span>
                  <strong>
                    {loading ? "—" : dashNum(dashboardData?.active_dihran)}
                  </strong>
                </div>
                <div className="girvi-ledger-stat">
                  <span>Closed</span>
                  <strong>
                    {loading ? "—" : dashNum(dashboardData?.closed_dihran)}
                  </strong>
                </div>
                <div className="girvi-ledger-stat">
                  <span>Principal</span>
                  <strong>
                    {loading ? "—" : formatGirviCurrency(principal)}
                  </strong>
                </div>
                <div className="girvi-ledger-stat">
                  <span>Interest received</span>
                  <strong>
                    {loading
                      ? "—"
                      : formatGirviCurrency(dashboardData?.received_interest)}
                  </strong>
                </div>
                <div className="girvi-ledger-stat">
                  <span>This month</span>
                  <strong>
                    {loading
                      ? "—"
                      : formatGirviCurrency(
                          dashboardData?.current_month_interest,
                        )}
                  </strong>
                </div>
                <div className="girvi-ledger-stat">
                  <span>Recovered</span>
                  <strong>
                    {loading ? "—" : `${recoveredPct.toFixed(0)}%`}
                  </strong>
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className="row g-4">
          {metals.map((metal) => (
            <div className="col-12 col-md-4" key={metal.key}>
              <article className="girvi-metal h-100">
                <div className="girvi-metal-head">
                  <span className="girvi-metal-icon" aria-hidden="true">
                    <Icon icon={metal.icon} width={16} />
                  </span>
                  <p className="girvi-metal-name">{metal.name}</p>
                </div>
                <p className="girvi-metal-amt">
                  {loading ? (
                    <span className="girvi-ledger-skel girvi-ledger-skel--sm" />
                  ) : (
                    formatGirviCurrency(metal.amount)
                  )}
                </p>
                <p className="girvi-metal-meta">
                  {loading
                    ? "—"
                    : `${formatGirviWeight(metal.weight)} · ${metal.items} items`}
                </p>
              </article>
            </div>
          ))}
        </div>

        <div className="row g-4">
          <div className="col-12">
            <TransactionHistoryTable
              loading={loading}
              data={dashboardData?.today_transaction || []}
              onViewAll={canList ? () => navigate("/girvitable") : undefined}
            />
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

export default GirviDashboard;
